import styled from '@emotion/styled';
import { Link, useRouterState } from '@tanstack/react-router';
import { Database, FileText, Key, LayoutDashboard, MessageSquare, X, Zap } from 'lucide-react';
import type { ComponentType } from 'react';
import { colors, elevation, spacing, typography } from '@/lib/design-tokens';
import { useUIStore } from '@/stores/ui-store';

const SIDEBAR_WIDTH = '240px';
const MOBILE_BREAKPOINT = '768px';

interface NavItem {
  path: string;
  label: string;
  icon: ComponentType<{ size: number; 'aria-hidden': 'true' }>;
}

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  { path: '/dashboard', label: 'ダッシュボード', icon: LayoutDashboard },
  { path: '/schemas', label: 'スキーマ管理', icon: Database },
  { path: '/content', label: 'コンテンツ管理', icon: FileText },
  { path: '/forms', label: 'フォーム管理', icon: MessageSquare },
  { path: '/api-keys', label: 'APIキー管理', icon: Key },
  { path: '/cache', label: 'キャッシュ管理', icon: Zap },
] as const;

const Overlay = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isVisible',
})<{ isVisible: boolean }>`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background-color: rgba(9, 30, 66, 0.54);
    z-index: 100;
  }
`;

const SidebarContainer = styled('aside', {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  width: ${SIDEBAR_WIDTH};
  background-color: ${colors.neutral[0]};
  border-right: 1px solid ${colors.neutral[200]};
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  flex-shrink: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 200;
    transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(-100%)')};
    transition: transform 0.25s ease;
    box-shadow: ${elevation.overlay};
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[300]} ${spacing[300]};
  border-bottom: 1px solid ${colors.neutral[200]};
  min-height: 56px;
`;

const Logo = styled.span`
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.h500};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.neutral[900]};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: ${colors.neutral[600]};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(9, 30, 66, 0.06);
  }

  &:focus-visible {
    outline: 2px solid ${colors.brand.blue[300]};
    outline-offset: 2px;
  }
`;

const Nav = styled.nav`
  flex: 1;
  padding: ${spacing[200]} 0;
  overflow-y: auto;
`;

const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const NavListItem = styled.li``;

const NavLinkWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>`
  margin: ${spacing[50]} ${spacing[200]};
  border-radius: 4px;
  overflow: hidden;

  ${({ isActive }) =>
    isActive &&
    `
    background-color: ${colors.brand.blue[50]};
  `}

  a {
    display: flex;
    align-items: center;
    gap: ${spacing[250]};
    padding: ${spacing[250]} ${spacing[300]};
    font-family: ${typography.fontFamily.default};
    font-size: ${typography.fontSize.body};
    text-decoration: none;
    border-radius: 4px;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:focus-visible {
      outline: 2px solid ${colors.brand.blue[300]};
      outline-offset: 2px;
    }
  }
`;

function Sidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { isSidebarOpen, setSidebarOpen } = useUIStore();

  const handleClose = () => {
    setSidebarOpen(false);
  };

  const handleNavClick = () => {
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  return (
    <>
      <Overlay isVisible={isSidebarOpen} onClick={handleClose} aria-hidden="true" />
      <SidebarContainer isOpen={isSidebarOpen}>
        <SidebarHeader>
          <Logo>CMS Admin</Logo>
          <CloseButton onClick={handleClose} aria-label="サイドバーを閉じる" type="button">
            <X size={18} aria-hidden="true" />
          </CloseButton>
        </SidebarHeader>
        <Nav aria-label="サイドバーナビゲーション">
          <NavList>
            {NAV_ITEMS.map((item) => {
              const isActive = pathname === item.path || pathname.startsWith(`${item.path}/`);
              const Icon = item.icon;
              return (
                <NavListItem key={item.path}>
                  <NavLinkWrapper isActive={isActive}>
                    <Link
                      to={item.path}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={handleNavClick}
                    >
                      <Icon size={18} aria-hidden="true" />
                      {item.label}
                    </Link>
                  </NavLinkWrapper>
                </NavListItem>
              );
            })}
          </NavList>
        </Nav>
      </SidebarContainer>
    </>
  );
}

export { Sidebar };
